// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import QtyField, { QtyFieldStyles } from "@eisinc/App/Components/Qty/QtyField";
import { getValidatedQty } from "@eisinc/App/Utilities/helpers";
import { UnitOfMeasureData } from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import { QTY_MAX_VALUE } from "@eisinc/XDL/constants";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import reset from "@insite/client-framework/Store/Components/ProductSelector/Handlers/Reset";
import searchProducts from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import setUnitOfMeasure from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetUnitOfMeasure";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    onSelectProduct: (productInfo: ProductInfo, product: ProductModel) => void;
    skipInventoryValidation?: boolean;
    denyFixedConfiguration?: boolean;
    selectButtonTitle?: string;
    productIsConfigurableMessage?: ReactNode;
    productIsUnavailableMessage?: ReactNode;
    customErrorMessage?: ReactNode;
    extendedStyles?: ProductSelectorStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const { isSearching, searchResults, selectedProductInfo, errorType } = getProductSelector(state);
    const product = getProductState(state, selectedProductInfo?.productId).value;
    return {
        isSearching,
        searchResults,
        selectedProductInfo,
        product,
        errorType,
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    searchProducts,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    reset,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductSelectorStyles {
    container?: GridContainerProps;
    searchGridItem?: GridItemProps;
    qtyGridItem?: GridItemProps;
    unitOfMeasureGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    qtyTextField?: TextFieldPresentationProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
    selectButton?: ButtonPresentationProps;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    clearIcon?: IconProps;
    // XNG-Changes
    qtyfield?: QtyFieldStyles;
    wrapper?: InjectableCss;
    searchDropdownWrapper?: InjectableCss;
    buttonWrapper?: InjectableCss;
}

export const productSelectorStyles: ProductSelectorStyles = {
    container: {
        gap: 10,
    },
    searchGridItem: {
        width: [12, 12, 4, 5, 5],
        css: css`
            position: relative;
        `,
    },
    qtyGridItem: {
        width: [3, 3, 2, 1, 1],
    },
    unitOfMeasureGridItem: {
        width: [9, 9, 3, 3, 3],
    },
    buttonGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    optionWrapper: {
        css: css`
            display: flex;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 50px;
            height: 50px;
            margin-right: 10px;
        `,
    },
    productImage: {
        width: "50px",
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        size: 14,
        css: css`
            margin-top: 5px;
        `,
    },
    selectButton: {
        css: css`
            margin-top: 30px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    clearIcon: {
        css: css`
            position: absolute;
            right: 40px;
            top: 45px;
            cursor: pointer;
        `,
    },
};

const ENTER_KEY = 13;

const ProductSelector = ({
    onSelectProduct,
    skipInventoryValidation,
    denyFixedConfiguration,
    selectButtonTitle,
    searchProducts,
    isSearching,
    searchResults,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    selectedProductInfo,
    product,
    errorType,
    extendedStyles,
    customErrorMessage,
    productIsConfigurableMessage,
    productIsUnavailableMessage,
    location,
    reset,
}: Props) => {
    const [qty, setQty] = useState("1");
    const [qtyHasChanged, setQtyHasChanged] = useState(false);
    const [errorMessage, setErrorMessage] = useState<ReactNode>("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [styles] = useState(() => mergeToNew(productSelectorStyles, extendedStyles));

    // <xng-change>
    const [minQty, setMinQty] = useState<number>(1);
    const [multipleSaleQty, setMultipleSaleQty] = useState<number>(1);
    const [selectedProductId, setSelectedProductId] = useState("");
    const [allowFractionalQty, setAllowFractionalQty] = React.useState(false);
    const regexp = allowFractionalQty ? /^[0-9]*[.,]?[0-9]*$/ : /^[0-9\b]+$/;

    const isValidNumeric = (number: string) => {
        if (number === "") {
            // backspace or delete case
            return true;
        }
        return regexp.test(number);
    };

    const unitOfMeasuresData: UnitOfMeasureData[] = JSON.parse(product?.properties.unitOfMeasureData || "[]");
    useEffect(() => {
        if (product && selectedProductInfo) {
            if (unitOfMeasuresData && selectedProductInfo.unitOfMeasure) {
                const selectedUOM = unitOfMeasuresData.filter(
                    uom => uom.UnitOfMeasure === selectedProductInfo.unitOfMeasure,
                );
                let computedMinQty: number;
                let computedMsq: number;
                if (selectedUOM.length) {
                    computedMinQty = parseFloat(selectedUOM[0].MinimumOrderQuantity) || 1;
                    computedMsq = parseFloat(selectedUOM[0].MultipleSaleQuantity) || 1;
                } else {
                    computedMinQty =
                        parseFloat(product?.properties?.minimumOrderQty) ||
                        parseFloat(product?.minimumOrderQty?.toString()) ||
                        selectedProductInfo.qtyOrdered ||
                        1;
                    computedMsq = parseFloat(product?.properties?.multipleSaleQty) || 1;
                }

                const isFractionalMoq = !Number.isInteger(computedMinQty);
                setAllowFractionalQty(isFractionalMoq);
                setMinQty(computedMinQty);
                setMultipleSaleQty(computedMsq);

                setTimeout(() => {
                    setQty(computedMinQty.toString());
                }, 200);

                return;
            }
            setQty(selectedProductInfo.qtyOrdered.toString());
        }
    }, [selectedProductInfo?.unitOfMeasure]);
    // </xng-change>

    useEffect(() => {
        reset();
    }, [location.pathname]);

    useEffect(() => {
        if (!searchResults) {
            return;
        }

        const newOptions = searchResults.map(product => ({
            optionText: product.title,
            optionValue: product.id || undefined,
            rowChildren: (
                <StyledWrapper {...styles.optionWrapper} data-test-selector={`productSelector_search_${product.id}`}>
                    <StyledWrapper {...styles.imageWrapper}>
                        <LazyImage {...styles.productImage} src={product.image} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.infoWrapper}>
                        <Typography {...styles.autocompleteTitleText} data-test-selector="productSelector_ProductTitle">
                            {product.displayTitle || product.title}
                        </Typography>
                        <Typography {...styles.autocompleteErpText}>
                            {product.displayErpNumber || product.erpNumber}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            ),
        }));
        setOptions(newOptions);
    }, [searchResults]);

    useEffect(() => {
        setErrorMessage(customErrorMessage);
    }, [customErrorMessage]);

    useEffect(() => {
        // Xng-Change
        setSelectedProductId(selectedProductInfo ? selectedProductInfo.productId : "");
        // if (selectedProductInfo && !qtyHasChanged) {
        //     setQty(`${selectedProductInfo.qtyOrdered}`);
        // }
    }, [selectedProductInfo]);

    useEffect(() => {
        switch (errorType) {
            case "productIsConfigurable":
                setErrorMessage(productIsConfigurableMessage || translate("Cannot select configurable products"));
                break;
            case "productIsUnavailable":
                setErrorMessage(productIsUnavailableMessage || translate("Product is unavailable"));
                break;
            default:
                setErrorMessage("");
                break;
        }
    }, [errorType]);

    // <xng-change>
    useEffect(() => {
        if (selectedProductInfo) {
            setQty(minQty > selectedProductInfo.qtyOrdered ? minQty.toString() : `${selectedProductInfo.qtyOrdered}`);
        }
    }, [minQty]);

    useEffect(() => {
        const isFractionalMoq = product?.properties?.isFractionalMoq?.toLowerCase() === "true";
        setAllowFractionalQty(isFractionalMoq);

        if (product) {
            let defaultMinQty = 1;
            let defaultMsq = 1;
            if (product.properties?.minimumOrderQty && parseFloat(product.properties?.minimumOrderQty)) {
                defaultMinQty = parseFloat(product.properties?.minimumOrderQty);
                setQty(product.properties?.minimumOrderQty);
            } else if (product.minimumOrderQty) {
                defaultMinQty = parseFloat(product.minimumOrderQty?.toString());
                setQty(product.minimumOrderQty?.toString());
            } else {
                setQty("1");
            }
            if (product.properties?.multipleSaleQty && parseFloat(product.properties?.multipleSaleQty)) {
                defaultMsq = parseFloat(product.properties?.multipleSaleQty);
            }
            setMinQty(defaultMinQty);
            setMultipleSaleQty(defaultMsq);
        }
    }, [product]);
    // </xng-change>

    const onSelectionChangeHandler = (value?: string) => {
        if (searchResults && searchResults.length > 0) {
            const variantParentId = searchResults.find(o => o.id === value)?.styleParentId;
            if (variantParentId) {
                setProduct({
                    productId: variantParentId,
                    variantId: value,
                    validateProduct: true,
                    skipInventoryValidation,
                    denyFixedConfiguration,
                });
            } else {
                setProduct({
                    productId: value,
                    validateProduct: true,
                    skipInventoryValidation,
                    denyFixedConfiguration,
                });
            }
        }
    };

    const debouncedSearchProducts = debounce((query: string) => {
        searchProducts({ query, relevancy: false });
    }, 300);

    const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        setErrorMessage("");
        setSearchTerm(event.target.value);

        if (selectedProductInfo) {
            clearProduct();
        }

        debouncedSearchProducts(event.target.value);
    };

    const selectProduct = () => {
        if (!selectedProductInfo || errorMessage || !product) {
            return;
        }

        const newProductInfo = { ...selectedProductInfo };
        newProductInfo.qtyOrdered = parseFloat(qty);

        onSelectProduct(newProductInfo, product);

        clearProduct();
        setQty("1");
        setQtyHasChanged(false);
        searchProducts({ query: "", relevancy: false });
        // xng-change
        updateAddToCartEvent(product);
    };

    // <xng-change>
    const getDataLayer = () => {
        if (!(window as any).dataLayer) {
            (window as any).dataLayer = [];
        }
        return (window as any).dataLayer;
    };

    const updateAddToCartEvent = (product: ProductModel) => {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }

        const data = {
            event: "addToCart",
            ecommerce: {
                currencyCode: "USD",
                add: {
                    products: [
                        {
                            name: product.productTitle,
                            id: product.properties["productErpNumber"],
                            price: product.unitListPriceDisplay,
                            brand: product.brand?.name || "",
                            category: product.properties["category"] || "",
                            quantity: qty,
                        },
                    ],
                },
            },
        };

        dataLayer.push(data);
    };
    // </xng-change>

    const uomChangeHandler = (unitOfMeasure: string) => {
        if (!selectedProductInfo) {
            return;
        }

        setUnitOfMeasure({ unitOfMeasure });
    };

    const onKeyPress = (event: React.KeyboardEvent) => {
        if (event.charCode === ENTER_KEY) {
            if (selectedProductInfo) {
                selectProduct();
            } else if (searchTerm && (!searchResults || searchResults.length === 0)) {
                setProduct({ searchTerm, skipInventoryValidation, denyFixedConfiguration });
            }
        }
    };

    const clearButtonHandler = () => {
        clearProduct();
        setQty("1");
        setQtyHasChanged(false);
        searchProducts({ query: "", relevancy: false });
    };

    // <xng-change>
    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (!isValidNumeric(event.currentTarget.value)) {
            event.preventDefault();
            return;
        }
        const value = event.target.value;
        setQty(value);
        setQtyHasChanged(true);
    };

    const qtyOnBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        if (!product || !selectedProductInfo) {
            return;
        }
        const value = parseFloat(event?.currentTarget?.value || qty) || 0;

        const selectedUOM = unitOfMeasuresData?.find(uom => uom.UnitOfMeasure === selectedProductInfo.unitOfMeasure);

        const minQty =
            (selectedUOM ? parseFloat(selectedUOM.MinimumOrderQuantity) : 0) ||
            parseFloat(product.properties?.minimumOrderQty || "0") ||
            parseFloat(product.minimumOrderQty?.toString() || "0") ||
            1;

        if (value <= 0 || value > QTY_MAX_VALUE) {
            setQty(minQty.toString());
            return;
        }

        const msq =
            (selectedUOM ? parseFloat(selectedUOM.MultipleSaleQuantity) : 0) ||
            parseFloat(product.properties?.multipleSaleQty || "0") ||
            1;

        const newValue = getValidatedQty(value, minQty, msq);

        setQty(newValue.toString());
    };
    // </xng-change>

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productSelector">
            <StyledWrapper {...styles.searchDropdownWrapper}>
                <DynamicDropdown
                    {...styles.searchDynamicDropdown}
                    label={translate("Search")}
                    onSelectionChange={onSelectionChangeHandler}
                    onInputChange={onInputChanged}
                    onKeyPress={onKeyPress}
                    filterOption={() => true}
                    // Xng-Change
                    selected={selectedProductId || product?.id}
                    isLoading={isSearching}
                    options={options}
                    error={errorMessage}
                    data-test-selector="productSelector_search"
                />
                {/* Xng-Change */}
                {/* {selectedProductInfo && <Icon {...styles.clearIcon} src={X} onClick={clearButtonHandler} />} */}
            </StyledWrapper>
            <StyledWrapper>
                {/* XNG-Changes: use QtyField to match with desgin */}
                {/* Xng-Change - using QtyField */}
                {/* <TextField
                    type="number"
                    min="0"
                    label={translate("QTY")}
                    value={qty}
                    onChange={e => {
                        setQty(e.currentTarget.value);
                        setQtyHasChanged(true);
                    }}
                    data-test-selector="productSelector_qty"
                /> */}
                <QtyField
                    value={qty}
                    label={translate("QTY")}
                    // xng-change - EIS-4192 - adding aria-label for accessibility
                    aria-label={translate("QTY")}
                    data-test-selector="productSelector_qty"
                    min="0"
                    maxLength={9}
                    onChange={qtyChangeHandler}
                    onBlur={qtyOnBlurHandler}
                    allowFractionalQty={allowFractionalQty}
                    multipleSaleQty={multipleSaleQty}
                    minimumOrderQty={minQty}
                    extendedStyles={styles.qtyfield}
                />
            </StyledWrapper>
            <StyledWrapper className="uom-wrapper">
                {product && selectedProductInfo && (
                    <ProductContext.Provider
                        value={{ product, productInfo: selectedProductInfo, onUnitOfMeasureChanged: uomChangeHandler }}
                    >
                        <ProductUnitOfMeasureSelect
                            extendedStyles={styles.unitOfMeasureSelect}
                            data-test-selector="productSelector_uom"
                        />
                    </ProductContext.Provider>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.buttonWrapper}>
                <Button
                    {...styles.selectButton}
                    onClick={() => selectProduct()}
                    disabled={!selectedProductInfo || !!errorMessage || Number(qty) < 1}
                    data-test-selector="productSelector_selectProduct"
                >
                    {selectButtonTitle || translate("Select Product")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductSelector);
